import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, MapPin, Navigation, Ticket } from 'lucide-react';
import { Img, Shimmer } from '@/components/ui';
import { formatINR } from '@/lib/catalog';
import { FillingFastBadge } from '@/components/event/FillingFast';

/**
 * Featured-event carousel. Takes its events from the page (which loads them
 * once via `useEventList`) so the hero never fires its own request.
 *
 * The first five, in the API's order — which is LK Admin → Event order:
 * pinned events first as arranged there, then the rest by date.
 */
const HeroSection = ({ events = [], loading }) => {
  const featured = events.slice(0, 5);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  // Auto-advance pauses while the visitor is hovering or has focus inside the
  // carousel (they are reading or about to click), and never runs for people
  // who asked the OS for reduced motion.
  const [paused, setPaused] = useState(false);
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const go = useCallback(
    (next) => {
      if (featured.length === 0) return;
      setDirection(next > index ? 1 : -1);
      setIndex(((next % featured.length) + featured.length) % featured.length);
    },
    [featured.length, index]
  );

  useEffect(() => {
    if (featured.length <= 1 || paused || reduceMotion) return undefined;
    const timer = setInterval(() => {
      setDirection(1);
      setIndex((i) => (i + 1) % featured.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [featured.length, paused, reduceMotion]);

  if (loading) {
    return (
      <section className="bg-white pb-10 pt-6">
        <div className="lk-container grid items-center gap-8 md:grid-cols-12">
          <div className="space-y-4 md:col-span-7">
            <Shimmer className="h-4 w-40" />
            <Shimmer className="h-12 w-4/5" />
            <Shimmer className="h-5 w-2/3" />
            <Shimmer className="h-12 w-48 rounded-xl" />
          </div>
          <Shimmer className="mx-auto aspect-[3/4] w-64 rounded-3xl md:col-span-5" />
        </div>
      </section>
    );
  }

  const event = featured[index];
  if (!event) return null;

  const variants = {
    hidden: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    visible: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <>
    <MobileFeatured events={featured} />
    <section
      className="relative hidden overflow-hidden bg-white pb-8 pt-6 sm:pt-8 md:block md:pb-12"
      aria-roledescription="carousel"
      aria-label="Featured events"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
    >
      {/* Blurred poster wash — keeps the section tinted by the current event */}
      <AnimatePresence initial={false}>
        <motion.div
          key={event.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="pointer-events-none absolute inset-0 hidden scale-110 bg-cover bg-center blur-3xl md:block"
          style={{ backgroundImage: `url(${event.thumbnail})` }}
        />
      </AnimatePresence>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-40 bg-gradient-to-t from-white via-white/85 to-transparent md:block" />

      <div className="lk-container relative z-10">
        <div className="mb-4 flex items-center justify-between md:mb-6">
          <p className="lk-eyebrow tracking-[0.18em]">Featured</p>
          {featured.length > 1 && (
            <div className="flex gap-2">
              <button
                type="button"
                aria-label="Previous featured event"
                onClick={() => go(index - 1)}
                className="lk-icon-btn"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next featured event"
                onClick={() => go(index + 1)}
                className="lk-icon-btn"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* initial={false}: the first slide paints fully visible on load; only
            later slide changes animate. Gating the page's opening content on
            a JS animation left it invisible whenever the animation stalled. */}
        <AnimatePresence custom={direction} mode="wait" initial={false}>
          <motion.div
            key={event.id}
            custom={direction}
            variants={variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.4, ease: 'easeOut' }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.6}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = Math.abs(offset.x) * velocity.x;
              if (swipe < -600) go(index + 1);
              else if (swipe > 600) go(index - 1);
            }}
            className="grid cursor-grab items-center gap-6 active:cursor-grabbing md:grid-cols-12 md:gap-12"
          >
            {/* Poster */}
            <div className="order-1 md:order-2 md:col-span-5">
              <Link to={`/event/${event.id}`} className="group block">
                <Img
                  src={event.thumbnail}
                  alt={event.title}
                  ratio="3 / 4"
                  className="mx-auto w-56 rounded-3xl shadow-cardHover ring-1 ring-black/5 sm:w-64 md:w-[320px]"
                  imgClassName="transition-transform duration-700 group-hover:scale-105"
                />
              </Link>
            </div>

            {/* Copy */}
            <div className="order-2 md:order-1 md:col-span-7">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-brand-tint px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand-secondary">
                  {event.dateLabel}
                  {event.timeLabel ? ` · ${event.timeLabel}` : ''}
                </span>
                {event.comingSoon && (
                  <span className="rounded-full bg-teaser px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    Coming soon
                  </span>
                )}
                {event.discountPct > 0 && !event.comingSoon && (
                  <span className="rounded-full bg-brand-primary px-3 py-1 text-xs font-bold text-white">
                    {event.discountPct}% OFF
                  </span>
                )}
                {!event.comingSoon && (event.fillingFast || event.lowStock) && !event.soldOut && (
                  <FillingFastBadge left={event.fillingFastLeft} size="md" />
                )}
              </div>

              <h1 className="text-display text-ink-900">
                {event.title}
              </h1>

              <p className="mt-3 flex items-start gap-2 text-sm text-ink-700 md:text-lg">
                <MapPin className="mt-1 h-4 w-4 shrink-0 text-ink-500" aria-hidden="true" />
                <span className="line-clamp-2 max-w-xl">
                  {[event.venueName, event.location?.address].filter(Boolean).join(' · ')}
                </span>
              </p>

              {event.distanceKm != null && (
                <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-700">
                  <Navigation className="h-3.5 w-3.5" />
                  {event.distanceKm} km away
                </p>
              )}

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
                <div>
                  <p className="lk-label">
                    {event.comingSoon ? 'On sale soon' : 'Starts at'}
                  </p>
                  <p className="tabular text-2xl font-bold text-ink-900">
                    {event.comingSoon
                      ? 'Tickets open soon'
                      : event.isFree
                        ? 'Free'
                        : `₹${formatINR(event.priceFrom)}`}
                  </p>
                </div>
                <Link
                  to={`/event/${event.id}`}
                  className="lk-btn-primary w-full rounded-full sm:w-auto"
                >
                  <Ticket className="h-4 w-4" />
                  {event.comingSoon ? 'View details' : 'Book tickets'}
                </Link>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {featured.length > 1 && (
          <div className="mt-6 flex justify-center md:mt-8">
            {featured.map((item, i) => (
              // The visible pill stays small; the button around it is a full
              // 44px target so the dots are tappable on a phone.
              <button
                key={item.id}
                type="button"
                aria-label={`Show ${item.title}`}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => go(i)}
                className="lk-focus group flex h-11 min-w-[28px] items-center justify-center rounded-full px-1"
              >
                <span
                  className={`block h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? 'w-8 bg-ink-900' : 'w-1.5 bg-ink-200 group-hover:bg-ink-400'
                  }`}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
    </>
  );
};

/**
 * Phones: a swipeable rail of posters, peeking at both edges so it reads as
 * scrollable at a glance. The desktop slideshow stacked poster, badges, title,
 * venue and price vertically — 768px on a 656px screen, so the first thing a
 * phone saw was one event and nothing to browse. On a poster-led product the
 * art already carries the name and date; the caption only adds what the art
 * can't be trusted to show (date, price).
 */
const MobileFeatured = ({ events }) => {
  if (events.length === 0) return null;
  return (
    <section aria-label="Featured events" className="bg-white pb-5 pt-4 md:hidden">
      <h2 className="lk-container mb-3 text-h3 text-ink-900">Featured events</h2>
      <div className="scrollbar-hide flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-4 px-4 pb-1">
        {events.map((event) => (
          <Link
            key={event.id}
            to={`/event/${event.id}`}
            className="lk-focus group w-[62%] max-w-[260px] shrink-0 snap-start rounded-2xl active:scale-[0.98]"
          >
            <div className="relative">
              <Img
                src={event.thumbnail}
                alt={event.title}
                ratio="3 / 4"
                className="rounded-2xl shadow-card ring-1 ring-black/5"
              />
              {(event.comingSoon || event.discountPct > 0) && (
                <span
                  className={`absolute left-2 top-2 rounded-full px-2.5 py-1 text-[11px] font-bold text-white shadow ${
                    event.comingSoon ? 'bg-teaser' : 'bg-brand-primary'
                  }`}
                >
                  {event.comingSoon ? 'Coming soon' : `${event.discountPct}% OFF`}
                </span>
              )}
            </div>
            <p className="mt-2.5 truncate text-xs font-semibold uppercase tracking-wide text-brand-secondary">
              {event.dateLabel}
            </p>
            <p className="mt-0.5 truncate text-[15px] font-semibold text-ink-900">{event.title}</p>
            <p className="tabular mt-0.5 text-sm text-ink-500">
              {event.comingSoon
                ? 'Tickets open soon'
                : event.isFree
                  ? 'Free entry'
                  : `From ₹${formatINR(event.priceFrom)}`}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
